/**
 * 개인정보 가리기 **잠금** 규칙 SSOT (티켓 #461).
 *
 * 무엇을 어떻게 가리는가는 `privacy-mask.ts` 가 진본이고, 이 파일은 **언제 잠기고 어떻게
 * 푸는가**를 정한다 — 매장이 정한 해제 암호, 손 뗀 뒤 다시 잠기기까지의 시간.
 *
 * 화면(프론트)과 서버가 같은 값을 봐야 하므로 shared 에 둔다.
 */

/** 매장대표가 고를 수 있는 "손 떼면 다시 가려지기까지" 시간(분). */
export const PRIVACY_IDLE_MINUTE_OPTIONS = [1, 3, 5, 10] as const;

/** 기본값 — 1분. 자리를 잠깐 비운 사이가 이 기능이 막으려는 상황이라 짧게 잡았다. */
export const DEFAULT_PRIVACY_IDLE_MINUTES = 1;

/** 해제 암호 길이 제한 — 짧으면 옆에서 보고 외우고, 너무 길면 검색칸에 치기 힘들다. */
export const PRIVACY_PASSWORD_MIN_LENGTH = 4;
export const PRIVACY_PASSWORD_MAX_LENGTH = 40;

/** 테넌트에 저장되는 설정 모양. 암호는 원문을 저장하지 않는다(소금 + 해시). */
export type PrivacyMaskConfig = {
  /** 해제 암호 해시(sha256(salt + password), hex). 없으면 아직 암호를 안 정한 매장. */
  passwordHash?: string;
  /** 해시에 쓴 소금(hex). */
  passwordSalt?: string;
  /** 손 떼고 다시 가려지기까지(분). 없으면 기본값. */
  idleMinutes?: number;
};

/** 화면에 내려주는 설정(암호 자체는 절대 내려가지 않는다). */
export type PrivacyMaskSettings = {
  /** 매장이 해제 암호를 정해 뒀는가. */
  hasPassword: boolean;
  /** 손 떼고 다시 가려지기까지(분). */
  idleMinutes: number;
  /**
   * 이 사용자에게 가리기가 걸리는가. 매장 소속이 아닌 본사 관리자는 가릴 고객 데이터도,
   * 해제할 매장 암호도 없다 — 걸어두면 풀 방법 없이 가려진 화면에 갇힌다.
   */
  applies: boolean;
};

/** 고를 수 없는 값이 들어오면 기본값으로 되돌린다. */
export function normalizeIdleMinutes(value: unknown): number {
  const n = typeof value === "number" ? value : Number(value);
  return (PRIVACY_IDLE_MINUTE_OPTIONS as readonly number[]).includes(n)
    ? n
    : DEFAULT_PRIVACY_IDLE_MINUTES;
}

/**
 * 검색칸에 친 글자를 암호 후보로 볼 것인가. 통합검색창을 암호 입력구로 겸용하므로
 * (티켓 #461 — 잠금 팝업을 따로 만들지 않는다는 것이 요구의 핵심) 자료를 찾는 평범한 검색어가
 * 매번 서버로 "암호 맞나" 확인을 나가지 않게 여기서 한 번 거른다.
 * 공백이 섞였거나 길이가 암호 범위를 벗어나면 그냥 검색어다.
 */
export function looksLikePrivacyPassword(value: string): boolean {
  const s = value.trim();
  if (s !== value.trim() || s === "") return false;
  if (/\s/.test(s)) return false;
  return (
    s.length >= PRIVACY_PASSWORD_MIN_LENGTH && s.length <= PRIVACY_PASSWORD_MAX_LENGTH
  );
}
